import type { TranslationTree } from '../../types';

export const traceability: TranslationTree = {
  traceability: {
    formPage: {
      hero: {
        badge: 'Traceability form',
        title: 'Validate the tool, then log your readings',
        description:
          'Review the scanned tool details, confirm its status, and record production parameters to keep traceability complete.',
        breadcrumb: 'Traceability data entry',
      },
      invalid: {
        title: 'Tool not found',
        description:
          'The provided identifier does not match any registered tool. Please check for a scanning or typing error.',
        backButton: 'Back to scan',
      },
      steps: {
        badge: 'Entry steps',
        title: 'Fill in the production conditions',
        description:
          'Complete the sections below to record a new trace. Your information will be validated step by step.',
      },
    },
    toolInfo: {
      badge: 'Tool validation',
      actions: {
        close: 'Close',
      },
      loading: 'Retrieving information…',
      errorDefault: 'Error while fetching the data.',
      fields: {
        reference: 'Reference',
        company: 'Company',
        totalTraces: 'Recorded traces',
        latestStatus: 'Latest status',
      },
      fallback: {
        notProvided: 'Not provided',
      },
      latestReasonPrefix: 'Latest reason:',
    },
    scanPage: {
      hero: {
        badge: 'Scan session',
        title: 'Identify your tools instantly',
        description:
          'Place the tool in front of the camera to detect its identifier automatically, or enter it manually if needed.',
        breadcrumb: 'Scan',
      },
      tabs: {
        camera: 'Camera',
        keyence: 'Keyence SR-X300',
      },
      scan: {
        title: 'Automatic scan',
        description:
          'Hold the tool in front of the camera. Once detected, the traceability form opens automatically.',
        overlay: {
          detected: 'Tool detected: {{id}}',
          waiting: 'Waiting for detection… keep the tool steady.',
          active: 'Scan active',
        },
        tips: {
          lighting: 'Use a neutral background and avoid glare to speed up code reading.',
          switchCamera: 'You can switch the camera at the bottom right when scanning from mobile.',
        },
      },
      manual: {
        title: 'Manual identifier entry',
        description:
          'Pick the tool from the list or type the ID printed on its marking.',
        label: 'Tool ID',
        placeholder: 'e.g. TL-4823',
        submit: 'Validate ID',
      },
      dev: {
        title: 'Development mode',
        description: 'Bypass scanning to test the traceability form without a camera.',
        button: 'Open test form',
      },
      keyence: {
        title: 'Keyence SR-X300 Scanner',
        description: 'Configure the smart camera and trigger a scan to read the DataMatrix code.',
        backendLabel: 'Backend URL',
        ipLabel: 'Camera IP Address',
        backendPlaceholder: 'e.g. https://toollife-datamatrix.local:44312',
        ipPlaceholder: 'e.g. 192.168.100.100',
        connect: 'Connect',
        disconnect: 'Disconnect',
        connecting: 'Connecting…',
        disconnecting: 'Disconnecting…',
        scan: 'Scan',
        scanning: 'Scanning…',
        cancel: 'Cancel',
        detected: 'Tool detected',
        accept: 'Accept and continue',
        error: 'Error',
        status: {
          connected: 'Connected to camera',
          disconnected: 'Not connected',
        },
        errors: {
          connectionFailed: 'Failed to connect to camera',
          disconnectionFailed: 'Failed to disconnect from camera',
          scanFailed: 'Scan failed',
          cancelFailed: 'Failed to cancel scan',
          networkError: 'Network error - check backend URL',
        },
        tips: {
          network: 'Ensure the Keyence camera is connected to the same network.',
          position: 'Position the tool in the camera field of view before triggering.',
          backend: 'The backend service manages the communication with the camera.',
        },
        advanced: {
          title: 'Advanced settings',
        },
        info: {
          title: 'How it works',
          description: 'The Keyence SR-X300 reads DataMatrix codes remotely.',
          step1: 'Enter the backend URL and camera IP address.',
          step2: 'Click "Connect" to establish connection with the camera.',
          step3: 'Press "Scan" to capture and decode the identifier.',
        },
      },
      camera: {
        title: 'Automatic scan',
        description:
          'Hold the tool in front of the camera. Once detected, the traceability form opens automatically.',
        detected: 'Tool detected',
        scanning: 'Scanning…',
        tips: {
          lighting: 'Use a neutral background and avoid glare to speed up code reading.',
          position: 'Keep the tool steady in the camera field of view.',
        },
      },
      addTool: {
        button: 'Create Tool',
      },
    },
    machineManager: {
      title: 'Machine management',
      description:
        'Add machines from your fleet, update their specifications, and sync the selection with the form.',
      confirmDelete: 'Confirm',
      errors: {
        nameRequired: 'Machine identifier is required.',
        selectBeforeDelete: 'Select a machine before deleting it.',
        notFound: 'The selected machine no longer exists.',
        apiNotReady: 'API not ready',
        fetchFailed: 'Failed to load machines',
        saveFailed: 'Failed to save machine',
        deleteFailed: 'Machine deletion is not yet implemented.',
        noData: 'No machine data returned',
      },
      form: {
        sectionTitle: 'Machine sheet',
        fields: {
          name: {
            label: 'Machine identifier',
            placeholder: 'e.g. DMG Mori DMC 65 monoBLOCK',
          },
          brand: {
            label: 'Brand',
            placeholder: 'e.g. DMG Mori',
          },
          model: {
            label: 'Model',
            placeholder: 'Enter the model',
          },
          serial: {
            label: 'Serial number',
            placeholder: 'Internal or manufacturer reference',
          },
          kafkaName: {
            label: 'Signal source',
            placeholder: 'No signal source',
            help: "The machine's measurement in the signals database (InfluxDB). The Signals page and the live predictions read it.",
            groupActive: 'Active now',
            groupHistory: 'History only',
            historyOnlyWarning:
              'No data in the last hour: live predictions will read the newest part recorded and say how old it is.',
            loadError: 'The list of signal sources could not be loaded.',
            retry: 'Retry',
          },
        },
        submit: {
          save: 'Save',
          update: 'Update',
          saving: 'Saving...',
        },
        new: 'New sheet',
      },
      helper:
        'Machines are stored locally to streamline data entry. Keep them updated whenever maintenance or changes occur.',
      list: {
        title: 'Registered machines',
        count: '{{count}} item(s)',
        empty: 'No machines registered yet.',
        serial: 'SN: {{serial}}',
        sourceBadgeTitle: 'Signal source',
      },
      delete: {
        button: 'Delete selection',
        deleting: 'Deleting...',
      },
    },
    processManager: {
      form: {
        sectionTitle: 'Process sheet',
        fields: {
          name: {
            label: 'Process name',
            placeholder: 'e.g. Finishing EGO2-40',
          },
          type: {
            label: 'Process type',
            standard: 'Standard',
            test: 'Test',
          },
          toolReference: {
            label: 'Tool reference',
            placeholder: '— select —',
            noOptions: 'No tool references available',
          },
          material: {
            label: 'Material',
            none: '— none —',
          },
          materialLot: {
            label: 'Material lot',
            none: 'No lot',
            placeholder: '— select lot —',
            disabled: 'Select a material first',
          },
          article: {
            label: 'Article',
            none: '— none —',
          },
          machines: {
            label: 'Machines',
            placeholder: '— add machine —',
          },
        },
        showToOperator: 'Show to operator',
        durationWarning: 'No machining duration is pre-filled — operators will be asked for the duration per piece on every trace using this process.',
        submit: {
          save: 'Save process',
          update: 'Save changes',
          saving: 'Saving...',
          duplicate: 'Duplicate',
          duplicateHint: 'Copy this process without saving',
        },
        new: 'New blank process',
        editingPill: 'Editing — {{name}}',
        notSet: '— Not set —',
        sections: {
          cuttingParameters: {
            title: 'Cutting parameters',
            toggleLabel: 'Toggle cutting parameters section',
          },
          machinedResults: {
            title: 'Machined results',
            toggleLabel: 'Toggle machined results section',
          },
          operatorInfo: {
            title: 'Operator info',
            toggleLabel: 'Toggle operator info section',
          },
          tooling: {
            title: 'Tooling & clamping',
            toggleLabel: 'Toggle tooling & clamping section',
          },
          countLabel: '{{set}}/{{total}} set',
        },
        prefillFields: {
          ae: {
            label: 'ae',
            placeholder: 'e.g. 2.5',
            unit: 'mm',
          },
          ap: {
            label: 'ap',
            placeholder: 'e.g. 5.0',
            unit: 'mm',
          },
          vc: {
            label: 'vc',
            placeholder: 'e.g. 120',
            unit: 'm/min',
          },
          fz: {
            label: 'fz',
            placeholder: 'e.g. 0.05',
            unit: 'mm/tooth',
          },
          machinedVolume: {
            label: 'Machined volume',
            placeholder: 'e.g. 10',
            unit: 'cm³',
          },
          machinedDistance: {
            label: 'Machined distance',
            placeholder: 'e.g. 50',
            unit: 'm',
          },
          machinedPieces: {
            label: 'Machined pieces',
            placeholder: 'e.g. 100',
            unit: 'count',
          },
          totalMachiningDuration: {
            label: 'Total machining duration',
            placeholder: 'e.g. 3600',
            unit: 's',
          },
          pieceMachiningDuration: {
            label: 'Per-piece duration',
            placeholder: 'e.g. 36',
            unit: 's',
          },
          malround: {
            label: 'Malround',
            placeholder: 'e.g. 5',
            unit: 'µm',
          },
          operator: {
            label: 'Operator',
            placeholder: 'e.g. J. Smith',
          },
          operation: {
            label: 'Operation',
            placeholder: 'e.g. Finishing pass',
          },
          lubType: {
            label: 'Lubrication type',
            placeholder: 'e.g. Emulsion',
          },
          lubBrand: {
            label: 'Lubricant brand',
            placeholder: 'e.g. Blaser B-Cool',
          },
          holderBrand: {
            label: 'Holder brand',
            placeholder: 'e.g. REGO-FIX',
          },
          holderType: {
            label: 'Holder type',
            placeholder: 'e.g. Collet chuck',
          },
          clampingType: {
            label: 'Clamping type',
            placeholder: 'e.g. Vise',
          },
          clampingBrand: {
            label: 'Clamping brand',
            placeholder: 'e.g. Hainbuch',
          },
        },
      },
      list: {
        title: 'Registered processes',
        count: '{{count}} process(es)',
        empty: 'No processes registered yet.',
        showArchived: 'Show archived',
        archivedBadge: 'Archived',
        searchPlaceholder: 'Search by name…',
        filterAll: 'All',
        filterStandard: 'Standard',
        filterTest: 'Test',
      },
      archive: {
        button: 'Archive',
        confirm: 'Confirm archive',
        archiving: 'Archiving...',
      },
      restore: {
        button: 'Restore',
      },
      errors: {
        apiNotReady: 'API not ready',
        fetchFailed: 'Failed to load processes',
        noData: 'No process data returned',
        nameRequired: 'Process name is required.',
        toolReferenceRequired: 'Tool reference is required.',
        saveFailed: 'Failed to save process',
      },
    },
    processSelection: {
      title: 'Pick a process to skip pre-known fields',
      skip: 'Start blank',
      prefilled: '{{count}} fields pre-filled',
      youFill: '{{count}} you fill',
      type: {
        standard: 'Standard',
        test: 'Test',
      },
      chip: {
        label: 'Template',
        hidden: '{{count}} fields hidden',
        change: 'Change',
        clear: 'Clear',
      },
      loading: 'Loading process templates…',
      errorTitle: 'Could not load process templates',
      errorDescription: 'You can still record a trace by filling the form manually.',
      errorRetry: 'Retry',
      sections: {
        cutting: 'Cutting parameters',
        machined: 'Machined results',
        operatorInfo: 'Operator & operation',
        machine: 'Machine',
        tooling: 'Tooling & clamping',
        material: 'Material',
        toolState: 'Tool state',
      },
      machineFallback: 'Select a machine…',
      emptyColumn: 'No processes of this type',
    },
    materialManager: {
      form: {
        sectionTitle: 'Material sheet',
        fields: {
          name: {
            label: 'Material name',
            placeholder: 'e.g. X5CrNi18-10 Stainless',
          },
          isoCategory: {
            label: 'ISO category',
            placeholder: '— select —',
          },
          designation: {
            label: 'Designation',
            placeholder: 'e.g. X5CrNi18-10',
          },
          description: {
            label: 'Description',
            placeholder: 'Optional notes about this material',
          },
        },
        submit: {
          save: 'Save',
          update: 'Update',
          saving: 'Saving...',
        },
        new: 'New material',
      },
      list: {
        title: 'Registered materials',
        count: '{{count}} material(s)',
        empty: 'No materials registered yet.',
        emptyTitle: 'No materials yet',
        emptyHint: 'Add the first material to start building your catalogue.',
        showArchived: 'Show archived',
        archivedBadge: 'Archived',
      },
      archive: {
        button: 'Archive',
        confirm: 'Confirm archive',
        archiving: 'Archiving...',
      },
      restore: {
        button: 'Restore',
      },
      errors: {
        apiNotReady: 'API not ready',
        fetchFailed: 'Failed to load materials',
        nameRequired: 'Material name is required.',
        isoCategoryRequired: 'ISO category is required.',
        saveFailed: 'Failed to save material',
        noData: 'No material data returned',
      },
    },
    articleManager: {
      form: {
        sectionTitle: 'Article sheet',
        fields: {
          name: {
            label: 'Article name',
            placeholder: 'e.g. Aluminium flange 40mm',
          },
        },
        submit: {
          save: 'Save',
          update: 'Update',
          saving: 'Saving...',
        },
        new: 'New article',
      },
      list: {
        title: 'Registered articles',
        count: '{{count}} article(s)',
        empty: 'No articles registered yet.',
        showArchived: 'Show archived',
        archivedBadge: 'Archived',
      },
      archive: {
        button: 'Archive',
        confirm: 'Confirm archive',
        archiving: 'Archiving...',
      },
      restore: {
        button: 'Restore',
      },
      errors: {
        apiNotReady: 'API not ready',
        fetchFailed: 'Failed to load articles',
        nameRequired: 'Article name is required.',
        saveFailed: 'Failed to save article',
        noData: 'No article data returned',
      },
    },
    form: {
      stepCounter: 'Step {{current}} of {{total}}',
      sending: 'Sending…',
      cancel: 'Cancel submission',
      errorDefault: 'An error occurred while retrieving data.',
      missingFields: '{{count}} field(s) must be filled before submitting.',
      missingFieldsNamed: 'Missing required fields: {{fields}}',
      scanPopup: {
        title: 'Scan a tool',
        description: 'Hold the tool in front of the camera to detect its identifier automatically.',
        helper: 'Keep the marking sharp and well lit to speed up the reading.',
        status: {
          waiting: 'Waiting for detection… keep the tool steady.',
          detected: 'Tool detected: {{id}}',
        },
        actions: {
          cancel: 'Cancel',
        },
      },
      retry: 'Try again',
      backHome: 'Back to home',
      success: 'Trace submitted successfully.',
      newScan: 'New scan',
      previous: 'Previous',
      next: 'Next',
      submit: 'Submit',
      stepAriaLabel: 'Step {{number}}: {{label}}, {{status}}',
      stepStatusCompleted: 'completed',
      stepStatusCurrent: 'current',
      stepStatusUpcoming: 'upcoming',
      validation: {
        requiredMultiTooltip: 'At least one field marked with a yellow * must be filled in.',
        requiredMultiAtLeastOne: 'At least one field must be completed.',
        checkboxAtLeastOne: 'Please select at least one option.',
      },
    },
    steps: {
      general: 'General',
      material: 'Material',
      machine: 'Machine',
      holder: 'Tool holder',
      clamping: 'Part clamping',
      lubrication: 'Lubrication',
      break: 'Tool status',
    },
    generalTab: {
      durationsTitle: 'Machining durations',
      durationsDescription:
        'Provide the total duration or the per-piece duration. Either value helps monitor tool wear.',
      durationsRequiredCounter: '1 of 2 required',
      productionTitle: 'Production',
      productionDescription:
        'Fill in at least one metric to describe the machining effort performed.',
      productionRequiredCounter: '1 of 3 required',
      totalDurationLabel: 'Total machining duration',
      pieceDurationLabel: 'Per-piece machining duration',
      duration: {
        hoursShort: 'h',
        hoursLabel: 'hours',
        minutesShort: 'min',
        minutesLabel: 'minutes',
        secondsShort: 's',
        secondsLabel: 'seconds',
        increase: 'Increase {{unit}}',
        decrease: 'Decrease {{unit}}',
      },
      piecesLabel: 'Number of parts',
      volumeLabel: 'Chip volume [cm³]',
      distanceLabel: 'Machined distance [m]',
      qualityTitle: 'Quality tracking',
      qualityDescription:
        'These optional fields help correlate quality indicators with cutting parameters.',
      malroundLabel: 'Runout (µm)',
      malroundPlaceholder: 'Not specified',
      operationLabel: 'Operation',
      operationPlaceholder: 'Select or type an operation…',
      operatorLabel: 'Lead operator',
      cutParameters: {
        title: 'Cut parameters',
        description: 'Optional cutting conditions for this operation.',
        aeLabel: 'ae (mm)',
        apLabel: 'ap (mm)',
        vcLabel: 'vc (m/min)',
        fzLabel: 'fz (mm/z)',
        placeholder: '–',
      },
    },
    materialTab: {
      materialTitle: 'Machined material',
      materialDescription:
        'Select the material from the catalogue. It helps us suggest relevant recommendations.',
      title: 'Material',
      picker: {
        label: 'Material',
        placeholder: 'Select a material…',
        searchPlaceholder: 'Search by name or designation…',
        noResults: 'No matching material found.',
        hint: 'Material missing? Add one in the Materials tab of the Workshop.',
      },
      lot: {
        title: 'Lot details',
        optional: 'optional',
        supplierLabel: 'Supplier',
        supplierPlaceholder: 'Supplier name',
        lotLabel: 'Lot number',
        lotPlaceholder: 'e.g. L2025-04',
        hint: 'Same supplier and lot for the same material is automatically deduplicated server-side.',
      },
      empty: {
        title: 'No materials yet',
        hint: 'No materials yet — add one in the Materials tab of the Workshop.',
      },
      prefill: {
        header: 'Pre-filled by process',
        locked: 'Locked by the selected process template. Pick a different process to change.',
        lot: {
          supplier: 'Supplier',
          lot: 'Lot',
        },
      },
      errors: {
        required: 'Please select a material.',
      },
      supplierLabel: 'Supplier',
      supplierPlaceholder: 'Supplier name',
      lotLabel: 'Lot number',
      lotPlaceholder: 'e.g. L2025-04',
    },
    machineTab: {
      sectionTitle: 'Selected machine',
      sectionDescription: 'Pick a saved machine or fill in the details manually.',
      dropdownLabel: 'Saved machine',
      dropdownPlaceholder: 'Choose a machine',
      helperText:
        'Manage your machine library or create a new entry directly from the manager. You can tweak the details below for this specific trace.',
      detailsTitle: 'Machine details for this trace',
      brandLabel: 'Brand',
      brandPlaceholder: 'e.g. DMG Mori',
      modelLabel: 'Model',
      modelPlaceholder: 'e.g. DMC 65 monoBLOCK',
      serialLabel: 'Serial number / Identifier',
      serialPlaceholder: 'Internal or manufacturer identifier',
      kafkaNameLabel: 'Signal source',
      summaryTitle: 'Selected card',
      summarySerial: 'SN: {{serial}}',
      openManager: 'Open machine manager',
      refreshList: 'Refresh list',
      listTitle: 'Saved machines',
      listCount: '{{count}} item(s)',
      emptyState: 'No machine saved yet.',
      deleteSelection: 'Delete selection',
    },
    lubricationTab: {
      title: 'Lubrication',
      description: 'Detail the method and product used to measure their impact on tool life.',
      typeLabel: 'Lubrication type',
      brandLabel: 'Brand / Product',
      brandPlaceholder: 'e.g. Blaser B-Cool',
      options: {
        dry: 'Dry machining',
        oil: 'Neat oil',
        emulsion: 'Emulsion',
        mql: 'Minimum quantity lubrication (MQL)',
        co2: 'CO₂',
        air: 'Air blast',
        other: 'Other',
      },
    },
    holderTab: {
      title: 'Tool holder',
      description:
        'Identify the tool holder to track how rigidity and balancing influence tool lifetime.',
      typeLabel: 'Tool-holder type',
      brandLabel: 'Brand / Model',
      brandPlaceholder: 'e.g. REGO-FIX powRgrip',
      options: {
        hotShrink: 'Hot shrink fit',
        coldShrink: 'Cold shrink fit',
        collet: 'Collet chuck',
        hydraulic: 'Hydraulic chuck',
        other: 'Other',
      },
    },
    clampingTab: {
      title: 'Part clamping',
      description: 'Specify how the part is held to interpret vibrations and potential drifts.',
      typeLabel: 'Clamping type',
      brandLabel: 'Brand / Model',
      brandPlaceholder: 'e.g. Hainbuch TOPlus mini',
      options: {
        vise: 'Vise',
        clamp: 'Clamp',
        fixture: 'Fixture',
        chuck: 'Chuck',
        rotarySteady: 'Rotary steady rest',
        fixedSteady: 'Fixed steady rest',
        collet: 'Collet',
        vacuum: 'Vacuum table',
        glue: 'Adhesive',
        gel: 'Gel support',
        other: 'Other',
      },
    },
    breakTab: {
      stateTitle: 'Observed condition',
      stateDescription:
        'Describe the tool condition at the time of the trace to contextualize the change.',
      stateLabel: 'Tool condition',
      reasonTitle: 'Change & follow-up',
      reasonDescription:
        'Select the reasons for replacement and add a note that remains useful for the shop floor.',
      reasonLabel: 'Reason for change',
      commentLabel: 'Note',
      commentPlaceholder: 'Free note for the production teams',
      stateOptions: {
        new: 'New',
        worn: 'Worn',
        broken: 'Broken',
        unknown: 'Unknown',
      },
      reasonOptions: {
        completeBrokenTool: 'Tool broken (complete failure)',
        brokenTeeth: 'Broken tooth/teeth',
        brokenPart: 'Workpiece broken',
        burr: 'Burr formation',
        badSurface: 'Poor surface finish',
        badDimension: 'Out-of-tolerance dimensions',
        endOfSeries: 'End of production run',
        productionStop: 'Production stop during active run',
        noise: 'Abnormal machining noise',
        maxToolLife: 'Reached planned tool life',
        partAppearance: 'Part appearance issue',
        vibration: 'Excessive vibration',
        other: 'Other',
      },
    },
    uploadImage: {
      pageTitle: 'Upload Image',
      pageDescription: 'Upload an image containing a DataMatrix code to scan',
      breadcrumb: 'Upload',
      dropZone: {
        prompt: 'Drag and drop an image here',
        browse: 'Browse files',
        hint: 'Supports PNG, JPG, and other image formats',
      },
      statusIdle: 'No image loaded',
      statusLoading: 'Loading image…',
      errorDecode: 'Error while decoding the image.',
      errorNoDimension: 'Error: the image has no dimensions.',
      statusScanning: 'Scanning…',
      statusDetected: 'Identifier detected: {{id}}',
      statusNotFound: 'No DataMatrix detected. Try another image.',
      imageAlt: 'Imported preview',
    },
    addToolModal: {
      title: 'Create New Tool',
      description: 'Register a new tool in the database before scanning.',
    },
    materials: {
      acier: 'Steel',
      acier_trempe: 'Hardened Steel',
      aluminium: 'Aluminum',
      chrome: 'Chrome-Cobalt/Phynox/Stellite',
      titane: 'Titanium',
      fonte: 'Cast Iron',
      nickel: 'Nickel Alloy',
      bronze: 'Bronze',
      laiton: 'Brass',
      or: 'Gold',
      super: 'Superalloys',
      argent: 'Silver',
      cube: 'CuBe',
      cuivre: 'Copper',
      durnico: 'Durnico',
      laiton_sans_plomb: 'Lead-free Brass',
      metal_lourd: 'Heavy Metal (tungsten)',
      céramique: 'Ceramic',
      composite_carbone: 'Carbon Fiber Composite',
      composite_verre: 'Glass Fiber Composite',
      graphite: 'Graphite',
      palladium: 'Palladium',
      platine: 'Platinum',
      polymere: 'Polymer',
      tantale: 'Tantalum',
      autre: 'Other',
      lt700: '<700 N/mm²',
      gt700: '>700 N/mm²',
      finemac: 'Finemac',
      inox: 'Stainless',
      lt58: 'Hardened <58HRc',
      lt65: 'Hardened <65HRc',
      lt8: '<8%Si',
      gt8: '>8%Si',
    },
    materialLotManager: {
      picker: {
        sectionTitle: 'Browse by material',
        label: 'Material',
        placeholder: 'Select a material…',
        count: '{{count}} materials available',
      },
      form: {
        sectionTitle: 'Find or create lot',
        idempotent: 'Idempotent',
        fields: {
          supplier: {
            label: 'Supplier',
            placeholder: 'e.g. Outokumpu',
            optional: '(optional)',
          },
          lotNumber: {
            label: 'Lot number',
            placeholder: 'e.g. L2025-04-001',
            optional: '(optional)',
          },
        },
        idempotencyHint:
          'The same supplier and lot number for this material always returns the same lot ID.',
        submit: {
          findOrCreate: 'Find or create',
          saving: 'Saving…',
          reset: 'Reset',
        },
        result: {
          title: 'Lot ready',
          idLabel: 'id #{{id}}',
          noSupplier: 'No supplier',
        },
      },
      list: {
        title: 'Lots for {{name}}',
        count: '{{count}} lots',
        loading: 'Loading lots',
        emptyTitle: 'No lots yet',
        emptyHint: 'Create one using the form on the left.',
        row: {
          noSupplier: 'No supplier',
        },
      },
      empty: {
        title: 'Select a material to view its lots',
        hint: 'Lots are scoped to a single material — pick one from the dropdown above to start adding or browsing supplier lots.',
      },
      errors: {
        loadFailed: 'Could not load lots for this material.',
        saveFailed: 'Could not save the lot. Please try again.',
      },
    },
    workshop: {
      hero: {
        badge: 'Workshop',
        title: 'Workshop',
        description: 'Register tools in the database and manage your machines.',
        breadcrumb: 'Workshop',
      },
      tabs: {
        tool: 'Register Tool',
        machines: 'Machines',
        articles: 'Articles',
        materials: 'Materials',
        lots: 'Material Lots',
        processes: 'Processes',
      },
      toolForm: {
        mode: {
          single: 'Single',
          batch: 'Batch',
        },
        single: {
          sectionTitle: 'Tool Information',
        },
        batch: {
          sectionTitle: 'Batch Registration',
          addToolId: 'Add Tool ID',
          toolIdPlaceholder: 'Tool ID #{{n}}',
        },
        fields: {
          toolId: {
            label: 'Tool ID',
            placeholder: 'e.g. EGO-001',
          },
          reference: {
            label: 'Reference',
            placeholder: 'e.g. LB-12345',
          },
          company: {
            label: 'Company',
            placeholder: 'Company name',
            autoFilled: 'Auto-filled from account',
          },
        },
        info: {
          single:
            'Creates a new tool entry in the database. Available for traceability scans immediately after creation.',
          batch:
            'Creates multiple tools sharing the same reference. Results are shown per tool ID.',
        },
        submit: {
          single: 'Create Tool',
          batch: 'Create {{count}} Tools',
        },
        success: 'Tool created successfully.',
        errors: {
          default: 'Failed to create tool.',
        },
        scan: {
          button: 'Scan DataMatrix',
          buttonNext: 'Scan Next',
          buttonRescan: 'Re-scan',
          connecting: 'Connecting…',
          scanning: 'Scanning…',
          cancel: 'Cancel',
          waitingOverlay: 'Waiting for DataMatrix — hold tool in front of the camera',
          statusDisconnected: 'Keyence not connected',
          statusConnected: 'Connected · {{ip}}',
          statusDetected: 'DataMatrix detected — verify the ID above',
          statusReady: 'Ready for next scan',
          successFlash: '{{toolId}} registered successfully',
        },
        session: {
          title: 'This session',
          count: '{{n}} registered',
          clear: 'Clear',
        },
        autoRegister: {
          label: 'Auto-register on scan',
          description: 'Scan → register → next scan automatically — no clicks between tools',
          registering: 'Registering…',
          registeringFlash: 'Registering {{toolId}}…',
        },
      },
    },
  },
};
